// src/components/annotation/strategies/BBoxStrategy.ts
import { AnnotationDataBoundingBox } from '../../../types/models/annotation'
import {
  GeometryStrategy,
  InteractionContext,
  ResizeHandleName,
  KeyModifiers,
} from './GeometryStrategy'
import { createAxisAlignedPoints, getMinMaxFromPoints } from '../../../utils/helpers'

function ensureAxisAlignedPoints(box: AnnotationDataBoundingBox): number[] {
  if (box.points && box.points.length === 8) return [...box.points]
  const xmin = box.xmin ?? 0
  const ymin = box.ymin ?? 0
  const xmax = box.xmax ?? xmin
  const ymax = box.ymax ?? ymin
  return createAxisAlignedPoints(xmin, ymin, xmax, ymax)
}

export const bboxStrategy: GeometryStrategy = {
  hitTest: (point, box) => {
    const xmin = box.xmin ?? 0
    const ymin = box.ymin ?? 0
    const xmax = box.xmax ?? 0
    const ymax = box.ymax ?? 0
    return point.x >= xmin && point.x <= xmax && point.y >= ymin && point.y <= ymax
  },

  getActiveHandle: (box, mouseX, mouseY, ctx: InteractionContext) => {
    const {
      imageWidth,
      imageHeight,
      canvasWidth,
      canvasHeight,
      handleSizeCanvas,
      hitPaddingCanvas,
    } = ctx
    // Convert canvas handle size to image space
    const imageToCanvasScaleX = canvasWidth / imageWidth
    const imageToCanvasScaleY = canvasHeight / imageHeight
    const handleSizeX = handleSizeCanvas / imageToCanvasScaleX
    const handleSizeY = handleSizeCanvas / imageToCanvasScaleY

    const xmin = box.xmin ?? 0
    const ymin = box.ymin ?? 0
    const xmax = box.xmax ?? 0
    const ymax = box.ymax ?? 0

    // Edge handles should be exactly 20% of the corresponding edge length
    const edgeWidth = (xmax - xmin) * 0.2
    const edgeHeight = (ymax - ymin) * 0.2

    const handles: Array<{ name: ResizeHandleName; x: number; y: number; w: number; h: number }> = [
      { name: 'topLeft', x: xmin, y: ymin, w: handleSizeX, h: handleSizeY },
      { name: 'topRight', x: xmax - handleSizeX, y: ymin, w: handleSizeX, h: handleSizeY },
      { name: 'bottomLeft', x: xmin, y: ymax - handleSizeY, w: handleSizeX, h: handleSizeY },
      {
        name: 'bottomRight',
        x: xmax - handleSizeX,
        y: ymax - handleSizeY,
        w: handleSizeX,
        h: handleSizeY,
      },
      // Edge handles: center segments along each side
      {
        name: 'top',
        x: xmin + (xmax - xmin) * 0.5 - edgeWidth / 2,
        y: ymin,
        w: edgeWidth,
        h: handleSizeY,
      },
      {
        name: 'bottom',
        x: xmin + (xmax - xmin) * 0.5 - edgeWidth / 2,
        y: ymax - handleSizeY,
        w: edgeWidth,
        h: handleSizeY,
      },
      {
        name: 'left',
        x: xmin,
        y: ymin + (ymax - ymin) * 0.5 - edgeHeight / 2,
        w: handleSizeX,
        h: edgeHeight,
      },
      {
        name: 'right',
        x: xmax - handleSizeX,
        y: ymin + (ymax - ymin) * 0.5 - edgeHeight / 2,
        w: handleSizeX,
        h: edgeHeight,
      },
    ]

    for (const handle of handles) {
      // Expand hit area by hitPaddingCanvas converted to image space
      const padX = hitPaddingCanvas / imageToCanvasScaleX
      const padY = hitPaddingCanvas / imageToCanvasScaleY
      const left = handle.x - padX
      const right = handle.x + handle.w + padX
      const top = handle.y - padY
      const bottom = handle.y + handle.h + padY
      if (mouseX >= left && mouseX <= right && mouseY >= top && mouseY <= bottom) {
        return handle.name
      }
    }
    return null
  },

  resize: (box, handle, mouseX, mouseY) => {
    const updated: AnnotationDataBoundingBox = { ...box }
    const points = ensureAxisAlignedPoints(updated)
    const newPoints = [...points]

    switch (handle) {
      case 'topLeft':
        newPoints[0] = mouseX
        newPoints[1] = mouseY
        newPoints[3] = mouseY
        newPoints[6] = mouseX
        break
      case 'topRight':
        newPoints[2] = mouseX
        newPoints[3] = mouseY
        newPoints[1] = mouseY
        newPoints[4] = mouseX
        break
      case 'bottomRight':
        newPoints[4] = mouseX
        newPoints[5] = mouseY
        newPoints[7] = mouseY
        newPoints[2] = mouseX
        break
      case 'bottomLeft':
        newPoints[6] = mouseX
        newPoints[7] = mouseY
        newPoints[5] = mouseY
        newPoints[0] = mouseX
        break
      case 'top':
        // Move top edge: adjust top-left and top-right y
        newPoints[1] = mouseY
        newPoints[3] = mouseY
        break
      case 'bottom':
        newPoints[5] = mouseY
        newPoints[7] = mouseY
        break
      case 'left':
        newPoints[0] = mouseX
        newPoints[6] = mouseX
        break
      case 'right':
        newPoints[2] = mouseX
        newPoints[4] = mouseX
        break
    }

    updated.points = newPoints
    const { xmin, ymin, xmax, ymax } = getMinMaxFromPoints(newPoints)
    updated.xmin = xmin
    updated.ymin = ymin
    updated.xmax = xmax
    updated.ymax = ymax
    return updated
  },

  normalizeAfterMouseUp: (box) => {
    if (!box.points || box.points.length !== 8) return box
    const points = box.points
    const xCoords = [points[0], points[2], points[4], points[6]]
    const yCoords = [points[1], points[3], points[5], points[7]]
    const minX = Math.min(...xCoords)
    const maxX = Math.max(...xCoords)
    const minY = Math.min(...yCoords)
    const maxY = Math.max(...yCoords)

    const cleanedPoints = [minX, minY, maxX, minY, maxX, maxY, minX, maxY]
    const { xmin, ymin, xmax, ymax } = getMinMaxFromPoints(cleanedPoints)
    return { ...box, points: cleanedPoints, xmin, ymin, xmax, ymax }
  },

  onKeyDown: (box, key: string, modifiers: KeyModifiers, _ctx: InteractionContext) => {
    const updated: AnnotationDataBoundingBox = { ...box }
    const points = ensureAxisAlignedPoints(updated)
    const { xmin, ymin, xmax, ymax } = getMinMaxFromPoints(points)
    const cx = (xmin + xmax) / 2
    const cy = (ymin + ymax) / 2

    const moveStepBase = 1
    const moveStep = modifiers.shift ? moveStepBase * 5 : moveStepBase

    // Proportional scale factor similar to OBB strategy
    const scaleStep = modifiers.shift ? 0.05 : 0.02
    const clampSize = (s: number) => Math.max(1, s)

    const applyFromBounds = (nxmin: number, nymin: number, nxmax: number, nymax: number) => {
      const p = [nxmin, nymin, nxmax, nymin, nxmax, nymax, nxmin, nymax]
      updated.points = p
      const mm = getMinMaxFromPoints(p)
      updated.xmin = mm.xmin
      updated.ymin = mm.ymin
      updated.xmax = mm.xmax
      updated.ymax = mm.ymax
    }

    const move = (dx: number, dy: number) => {
      applyFromBounds(xmin + dx, ymin + dy, xmax + dx, ymax + dy)
    }

    const scaleSymmetric = (axis: 'u' | 'v', sign: 1 | -1) => {
      const halfW = (xmax - xmin) / 2
      const halfH = (ymax - ymin) / 2
      if (axis === 'u') {
        const factor = 1 + sign * scaleStep
        const nHalfW = clampSize(halfW * factor)
        const nxmin = cx - nHalfW
        const nxmax = cx + nHalfW
        applyFromBounds(nxmin, ymin, nxmax, ymax)
      } else {
        const factor = 1 + sign * scaleStep
        const nHalfH = clampSize(halfH * factor)
        const nymin = cy - nHalfH
        const nymax = cy + nHalfH
        applyFromBounds(xmin, nymin, xmax, nymax)
      }
    }

    switch (key) {
      // Movement
      case 'ArrowUp':
        if (modifiers.alt) scaleSymmetric('v', 1)
        else move(0, -moveStep)
        return updated
      case 'ArrowDown':
        if (modifiers.alt) scaleSymmetric('v', -1)
        else move(0, moveStep)
        return updated
      case 'ArrowLeft':
        if (modifiers.alt) scaleSymmetric('u', -1)
        else move(-moveStep, 0)
        return updated
      case 'ArrowRight':
        if (modifiers.alt) scaleSymmetric('u', 1)
        else move(moveStep, 0)
        return updated
      default:
        return null
    }
  },
}
